import {useState, useMemo, useContext} from 'react';
import Select from 'react-select';

import {CircularProgress, Modal, Slider} from '@mui/material';
import {Close as CloseIcon} from '@mui/icons-material';

import ConfirmationModal from '../modal/ConfirmationModal';
import AppContext from '../../context/AppContext';
import {dropdownStylesConfig} from '../form/dropdown/Dropdown';
import {User} from '../../types';
import {request, URLs} from '../../api';
import {UserSettings} from '../../types/entities/UserSettings';

export type EditorUserSettings = {
  version: number;
  enableMinimap: boolean;
  enableVimMode: boolean;
  lineNumbers: 'on' | 'off' | 'relative' | 'interval';
  fontSize: number;
  tabSize: number;
  wordWrap: 'on' | 'off' | 'wordWrapColumn' | 'bounded';
};

export const DEFAULT_EDITOR_SETTINGS: EditorUserSettings = {
  version: 1,
  enableMinimap: true,
  enableVimMode: false,
  lineNumbers: 'on',
  fontSize: 14,
  tabSize: 4,
  wordWrap: 'on',
};

const toggleSettings: Array<{key: keyof EditorUserSettings; label: string}> = [
  {key: 'enableMinimap', label: 'Enable Minimap'},
  {key: 'enableVimMode', label: 'Enable Vim Motions'},
];

const lineNumbersOptions = [
  {value: 'on', label: 'Normal'},
  {value: 'off', label: 'Off'},
  {value: 'relative', label: 'Relative'},
  {value: 'interval', label: 'Interval'},
];

const wordWrapOptions = [
  {value: 'on', label: 'Normal'},
  {value: 'off', label: 'Off'},
  {value: 'wordWrapColumn', label: 'Word Wrap Column'},
  {value: 'bounded', label: 'Bounded'},
];

type Props = {onClose: (newSettings: EditorUserSettings | null) => void};

export default function EditorSettingsModal({onClose}: Props) {
  const context = useContext(AppContext);
  const {user: contextUser, dispatchError} = useContext(AppContext);

  const dropdownStyles = dropdownStylesConfig();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [isClosing, setIsClosing] = useState(false);
  const [settings, setSettings] = useState<EditorUserSettings>(
    contextUser && contextUser.settings?.editorSettings
      ? {
          ...DEFAULT_EDITOR_SETTINGS,
          ...contextUser.settings.editorSettings,
        }
      : DEFAULT_EDITOR_SETTINGS
  );

  const handleSaveSettings = (newEditorSettings: EditorUserSettings) => {
    if (contextUser) {
      request<{}, {settings: UserSettings}, {settings: UserSettings}>(URLs.updateUserSettings(contextUser.id), {
        method: 'PATCH',
        body: {settings: {...contextUser.settings, editorSettings: newEditorSettings}},
        successCallback: (response) => {
          // Eduard: This errors when I deconstruct the context and grab the updateUser method ({updateUser} = useContext...)
          // for some reason, maybe because of the usage of classes in the context?
          context.updateUser(new User({...contextUser, settings: response.settings}));
          setIsLoading(false);

          onClose(settings);
        },
        errorCallback(error) {
          console.error('Error updating user settings:', error);
          dispatchError({errorMessage: error.message});
          setIsLoading(false);
          onClose(null);
        },
      });
    }
  };

  const settingsChanged = useMemo(() => {
    const initialSettings =
      contextUser && contextUser.settings?.editorSettings
        ? {
            ...DEFAULT_EDITOR_SETTINGS,
            ...contextUser.settings.editorSettings,
          }
        : DEFAULT_EDITOR_SETTINGS;

    return JSON.stringify(settings) !== JSON.stringify(initialSettings);
  }, [settings, contextUser]);

  const handleCloseAttempt = () => {
    settingsChanged ? setIsClosing(true) : onClose(null);
  };

  return (
    <>
      <Modal open onClose={handleCloseAttempt}>
        <div className="bg-background-default rounded-xl h-fit w-[500px] absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 outline-none p-6 select-none">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-semibold">Editor Settings</h2>
            <button onClick={handleCloseAttempt} className="p-1 rounded">
              <CloseIcon fontSize="small" />
            </button>
          </div>

          <div className="space-y-4">
            <div className="flex flex-col gap-4">
              {toggleSettings.map(({key, label}) => (
                <div key={key}>
                  <label
                    className={
                      'flex items-center justify-between px-3 py-4 rounded-lg cursor-pointer bg-[#384D58] hover:bg-[#43575f] transition-colors'
                    }
                  >
                    <span className="text-gray-100">{label}</span>
                    <input
                      type="checkbox"
                      className="form-checkbox h-6 w-6 text-blue-500"
                      checked={settings[key] as boolean}
                      onChange={(e) => setSettings((s) => ({...s, [key]: e.target.checked}))}
                    />
                  </label>
                </div>
              ))}

              <div>
                <label className="block mb-1">Font Size: {settings.fontSize}px</label>
                <Slider
                  value={settings.fontSize}
                  min={8}
                  max={24}
                  step={1}
                  valueLabelDisplay="auto"
                  onChange={(_, v) =>
                    setSettings((s) => ({
                      ...s,
                      fontSize: Array.isArray(v) ? v[0] : v,
                    }))
                  }
                />
              </div>

              <div>
                <label className="block mb-1">Tab Size: {settings.tabSize} spaces</label>
                <Slider
                  value={settings.tabSize}
                  min={2}
                  max={8}
                  step={1}
                  valueLabelDisplay="auto"
                  onChange={(_, v) =>
                    setSettings((s) => ({
                      ...s,
                      tabSize: Array.isArray(v) ? v[0] : v,
                    }))
                  }
                />
              </div>

              <div>
                <label className="block mb-1">Word Wrap</label>
                <Select
                  options={wordWrapOptions}
                  value={wordWrapOptions.find((option) => option.value === settings.wordWrap)}
                  onChange={(option) =>
                    setSettings((s) => ({
                      ...s,
                      wordWrap: (option as {value: EditorUserSettings['wordWrap']}).value,
                    }))
                  }
                  isSearchable={false}
                  styles={dropdownStyles}
                />
              </div>

              <div>
                <label className="block mb-1">Line Numbers</label>
                <Select
                  options={lineNumbersOptions}
                  value={lineNumbersOptions.find((option) => option.value === settings.lineNumbers)}
                  onChange={(option) =>
                    setSettings((s) => ({
                      ...s,
                      lineNumbers: (option as {value: EditorUserSettings['lineNumbers']}).value,
                    }))
                  }
                  isSearchable={false}
                  styles={dropdownStyles}
                  menuPlacement="top"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end mt-6">
            <button
              onClick={() => handleSaveSettings(settings)}
              disabled={!settingsChanged || isLoading}
              className={`px-2 py-3 w-40 text-gray-200 font-semibold bg-primary-main rounded-lg disabled:bg-gray-600 disabled:bg-opacity-40 disabled:border-gray-400 disabled:border-opacity-10 disabled:cursor-not-allowed disabled:text-opacity-50 border border-green-800 hover:bg-green-800 transition-all disabled:pointer-events-none `}
            >
              {isLoading ? (
                <span className="flex items-center gap-3 justify-center">
                  <CircularProgress size="1.5rem" color="success" />
                </span>
              ) : (
                'Save Settings'
              )}
            </button>
          </div>
        </div>
      </Modal>

      {isClosing && (
        <ConfirmationModal
          variant="continue"
          title="Unsaved Changes"
          message="You have unsaved changes. Close without saving?"
          onAccept={() => {
            setIsClosing(false);
            onClose(null);
          }}
          onClose={() => setIsClosing(false)}
        />
      )}
    </>
  );
}
